import React, { createContext, useContext, useCallback, useState, useEffect, useRef } from 'react';
import { dictionaries, languages } from './translations';
import settingsService from '../services/settingsService';
import authService from '../services/authService';

const DEFAULT_LANG = 'hu';
const STORAGE_KEY = 'app_language';

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  t: (key) => key,
  setLang: () => {},
  languages: []
});

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(DEFAULT_LANG);

  // Track if initial server fetch happened
  const initializedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        // Try server value first
        const resp = await settingsService.getLanguage();
        const serverLang = resp.data?.language;
        if (!cancelled && serverLang && dictionaries[serverLang]) {
          setLangState(serverLang);
          localStorage.setItem(STORAGE_KEY, serverLang);
        }
      } catch (e) {
        // Fallback to localStorage if server unreachable
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!cancelled && stored && dictionaries[stored]) {
          setLangState(stored);
        }
      } finally {
        initializedRef.current = true;
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const setLang = useCallback(async (newLang) => {
    if (!dictionaries[newLang]) return;
    setLangState(newLang);
    localStorage.setItem(STORAGE_KEY, newLang);
    // If user is admin or manager attempt to persist globally
    try {
      const currentUser = authService.getCurrentUser();
      const role = currentUser?.role;
      if (role === 'admin' || role === 'manager') {
        await settingsService.updateLanguage(newLang);
      }
    } catch (e) {
      // Non-fatal
      console.warn('Failed to persist language to server', e);
    }
  }, []);

  const t = useCallback((key) => {
    const parts = key.split('.');
    const access = (dict) => parts.reduce((acc, p) => (acc && acc[p] !== undefined ? acc[p] : undefined), dict);
    const primary = access(dictionaries[lang]);
    if (primary !== undefined) return primary;
    const fallback = access(dictionaries[DEFAULT_LANG]);
    return fallback !== undefined ? fallback : key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, languages }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
